import {Injectable} from "@angular/core";
import {BehaviorSubject, from, fromEvent, mergeMap, Observable, takeUntil, throttleTime, timer} from "rxjs";
import {filter, map} from "rxjs/operators";
import {DateTime} from "luxon";
import {SessionNames} from "../../shared/session/session-names";
import {AppConfigService} from "../app-config.service";

@Injectable({
  providedIn: 'root',
})
export class LastActiveService {
  private events: string[] = ['keydown', 'click', 'wheel', 'mousemove'];

  private lastActive: BehaviorSubject<DateTime>;
  public lastActive$: Observable<DateTime>;

  constructor(private appConfigService: AppConfigService) {
    const lastActiveDate = this.getLastActiveFromLocalStorage() ?? DateTime.now();
    this.lastActive = new BehaviorSubject<DateTime>(lastActiveDate);
    this.lastActive.next(DateTime.now());
    this.lastActive$ = this.lastActive.asObservable();

    // Activity is otherwise tracked per-tab only (DOM events on this tab's own document).
    // With one shared session cookie across tabs, an idle-but-authenticated tab would
    // then hit its own inactivity timeout and log out the whole session even while the
    // user is actively working in another tab. The 'storage' event fires in every OTHER
    // tab (not the one that wrote the value), so this keeps every tab's idle clock in
    // sync with activity happening anywhere in the app.
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(filter(event => event.key === SessionNames.ACTIVITY_DATETIME && !!event.newValue))
      .subscribe(event => {
        const activeDate = DateTime.fromMillis(Number(event.newValue));
        if (activeDate.isValid && activeDate > this.lastActive.value) {
          this.lastActive.next(activeDate);
        }
      });
  }

  public setUp() {
    if (!this.appConfigService.noActivityEventCheckDuration) {
      timer(250)
        .subscribe(() => this.setUp());
      return;
    }
    from(this.events).pipe(mergeMap(event => fromEvent(document, event)),
      throttleTime(this.appConfigService.noActivityEventCheckDuration)).subscribe(_ => this.recordLastActiveDate());
  }

  private recordLastActiveDate() {
    var currentDate = DateTime.now();
    localStorage.setItem(SessionNames.ACTIVITY_DATETIME, currentDate.toMillis() + '');
    this.lastActive.next(currentDate);
  }

  private getLastActiveFromLocalStorage(): DateTime | null {
    const valueFromStorage = localStorage.getItem(SessionNames.ACTIVITY_DATETIME);
    if (!valueFromStorage) {
      return null;
    }

    return DateTime.fromMillis(Number(valueFromStorage));
  }

  public getLastActiveDate(): DateTime {
    return this.lastActive.value;
  }
}
